import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Lock } from "lucide-react";

const STATUS_STYLES = {
  sold: { background: "#FFC107", color: "#0A0A0A" },
  in_talks: { background: "rgba(255,255,255,0.08)", color: "white" },
  available: { background: "#FF1493", color: "#0A0A0A" },
};

function StatusBadge({ status, sponsorName }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.available;
  return (
    <>
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display tracking-[0.2em] uppercase"
        style={style}
      >
        {status === "sold" && <Lock size={10} />}
        {status}
      </span>
      {sponsorName && (
        <div className="text-[11px] mt-1 text-white/70 truncate">{sponsorName}</div>
      )}
    </>
  );
}

function InventoryRow({ slot, sponsorInput, onSponsorInputChange, onSell, onRelease }) {
  const isAvailable = slot.status === "available";
  return (
    <div
      className="py-4 grid md:grid-cols-12 gap-3 items-center"
      data-testid={`inv-${slot.id}`}
    >
      <div className="md:col-span-3">
        <div className="font-display text-2xl uppercase">{slot.city}</div>
        <div className="text-[10px] font-mono-tactical text-white/50 uppercase tracking-[0.2em] mt-1">
          {slot.state} ·{" "}
          {new Date(slot.scheduled_date).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          })}
        </div>
      </div>
      <div className="md:col-span-3 text-sm">
        <div>{slot.tier}</div>
        <div className="font-display text-xl text-white">${slot.price.toLocaleString()}</div>
      </div>
      <div className="md:col-span-2">
        <StatusBadge status={slot.status} sponsorName={slot.sponsor_name} />
      </div>
      <div className="md:col-span-4 flex flex-wrap gap-2 items-center">
        {isAvailable ? (
          <>
            <input
              className="tac-input flex-1 min-w-[140px] text-xs"
              placeholder="Sponsor name"
              value={sponsorInput}
              onChange={(e) => onSponsorInputChange(slot.id, e.target.value)}
              data-testid={`inv-sponsor-${slot.id}`}
            />
            <button
              onClick={() => onSell(slot, "in_talks")}
              className="btn-ghost text-[10px] px-3 py-2"
              data-testid={`inv-talk-${slot.id}`}
            >
              In talks
            </button>
            <button
              onClick={() => onSell(slot, "sold")}
              className="btn-blaze text-[10px] px-3 py-2"
              data-testid={`inv-sell-${slot.id}`}
            >
              Mark sold
            </button>
          </>
        ) : (
          <button
            onClick={() => onRelease(slot)}
            className="btn-ghost text-[10px] px-3 py-2"
            data-testid={`inv-release-${slot.id}`}
          >
            Release
          </button>
        )}
      </div>
    </div>
  );
}

export default function SponsorshipInventoryCard() {
  const [inventory, setInventory] = useState([]);
  const [sponsorInputs, setSponsorInputs] = useState({});

  const load = useCallback(
    () => api.get("/sponsorship/inventory").then((r) => setInventory(r.data || [])),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const setSponsorInput = (id, value) =>
    setSponsorInputs((prev) => ({ ...prev, [id]: value }));

  const sellSlot = async (slot, status) => {
    const sponsor = (sponsorInputs[slot.id] || "").trim();
    if (!sponsor) {
      toast("Add a sponsor name first.");
      return;
    }
    try {
      await api.post(`/admin/sponsorships/${slot.id}/sell`, { sponsor_name: sponsor, status });
      toast(`${slot.city} → ${status === "sold" ? "Sold" : "In talks"} (${sponsor})`);
      setSponsorInput(slot.id, "");
      await load();
    } catch (err) {
      console.warn("sponsorship sell failed:", err?.response?.status || err?.message);
      toast("Failed to update slot.");
    }
  };

  const releaseSlot = async (slot) => {
    try {
      await api.post(`/admin/sponsorships/${slot.id}/release`);
      toast(`${slot.city} released`);
      await load();
    } catch (err) {
      console.warn("sponsorship release failed:", err?.response?.status || err?.message);
      toast("Failed to release.");
    }
  };

  const counts = {
    available: inventory.filter((s) => s.status === "available").length,
    sold: inventory.filter((s) => s.status === "sold").length,
    in_talks: inventory.filter((s) => s.status === "in_talks").length,
  };

  return (
    <div className="tac-card p-7 mt-8" data-testid="sponsorship-inventory-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-2">
            <Calendar size={12} /> Route inventory
          </div>
          <h2 className="font-display text-3xl uppercase mt-2">City sponsorship slots</h2>
        </div>
        <div className="font-mono-tactical text-xs text-white/50 uppercase tracking-[0.25em]">
          {counts.available} open · {counts.sold} sold · {counts.in_talks} in talks
        </div>
      </div>

      <div className="mt-6 divide-y divide-white/5">
        {inventory.map((slot) => (
          <InventoryRow
            key={slot.id}
            slot={slot}
            sponsorInput={sponsorInputs[slot.id] || ""}
            onSponsorInputChange={setSponsorInput}
            onSell={sellSlot}
            onRelease={releaseSlot}
          />
        ))}
      </div>
    </div>
  );
}
